import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RemoteAlertGroup, AlertRecipient, AlertSchedule } from '../../types/remoteAlerts';
import { User } from '../../types/user';
import { useGetZonesQuery, useGetZoneSensorsQuery } from '../../services/zoneApi';
import { useGetSensorListQuery } from '../../services/sensorApi';
import { useGetUsersQuery } from '../../services/userApi';
import {
  useAddRemoteAlertGroupMutation,
  useUpdateRemoteAlertGroupMutation,
} from '../../services/dataApi';
import { formatRtkMutationError } from '../../utils/formatRtkMutationError';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';

interface RemoteAlertGroupDialogProps {
  onClose: () => void;
  onSave: () => void;
  group?: RemoteAlertGroup;
}

const RemoteAlertGroupDialog: React.FC<RemoteAlertGroupDialogProps> = ({ onClose, onSave, group }) => {
  const [name, setName] = useState('');
  const [sensorIds, setSensorIds] = useState<number[]>([]);
  const [recipients, setRecipients] = useState<AlertRecipient[]>([]);
  const [notifyTemperature, setNotifyTemperature] = useState(true);
  const [notifyBattery, setNotifyBattery] = useState(true);
  const [notifyMissingData, setNotifyMissingData] = useState(true);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  // Per-day windows: Record<dayNumber, {start,end}[]>
  // Days 0=Sun 1=Mon … 6=Sat; displayed Mon–Sun
  type DayWin = { start: string; end: string };
  const defaultDayWindows = (): Record<number, DayWin[]> => ({
    0: [], 1: [{ start: '08:00', end: '20:00' }], 2: [{ start: '08:00', end: '20:00' }],
    3: [{ start: '08:00', end: '20:00' }], 4: [{ start: '08:00', end: '20:00' }],
    5: [{ start: '08:00', end: '20:00' }], 6: [],
  });
  const [dayWindows, setDayWindows] = useState<Record<number, DayWin[]>>(defaultDayWindows);
  const [isSaving, setIsSaving] = useState(false);
  const [addRemoteAlertGroup] = useAddRemoteAlertGroupMutation();
  const [updateRemoteAlertGroup] = useUpdateRemoteAlertGroupMutation();

  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const { data: zones = [] } = useGetZonesQuery();
  const { data: zoneSensors = [], isLoading: sensorsLoading } = useGetZoneSensorsQuery(selectedZoneId!, {
    skip: !selectedZoneId,
    refetchOnMountOrArgChange: true,
  });
  const { data: allSensors = [] } = useGetSensorListQuery();
  const { data: users = [], isLoading: usersLoading } = useGetUsersQuery();
  const user = useSelector((state: RootState) => state.auth.user);

  // id → name map built from the full sensor list (cached, 5-min TTL)
  const sensorNameMap = useMemo(() => {
    const map = new Map<number, string>();
    allSensors.forEach(s => map.set(Number(s.id), s.name || `Sensor ${s.id}`));
    return map;
  }, [allSensors]);

  // Initialise name, sensor selection, notify flags, and schedule from the group
  useEffect(() => {
    if (group) {
      setName(group.name);
      setSensorIds(group.sensorIds || []);
      setNotifyTemperature(group.notifyTemperature ?? true);
      setNotifyBattery(group.notifyBattery ?? true);
      setNotifyMissingData(group.notifyMissingData ?? true);
      const s = group.schedule;
      if (s?.enabled) {
        setScheduleEnabled(true);
        const fresh = defaultDayWindows();
        // Zero out defaults before loading saved data
        for (const k of Object.keys(fresh)) fresh[Number(k)] = [];
        let windows = s.windows;
        // Backward-compat: old format used days/startTime/endTime
        if (!Array.isArray(windows) && Array.isArray((s as any).days)) {
          const old = s as any;
          windows = old.days.map((d: number) => ({ day: d, start: old.startTime || '08:00', end: old.endTime || '20:00' }));
        }
        for (const w of (windows ?? [])) {
          if (!fresh[w.day]) fresh[w.day] = [];
          fresh[w.day].push({ start: w.start, end: w.end });
        }
        setDayWindows(fresh);
      } else {
        setScheduleEnabled(false);
        setDayWindows(defaultDayWindows());
      }
    }
  }, [group]);

  // Set recipients once users have loaded
  useEffect(() => {
    if (group && users.length > 0) {
      const mappedRecipients = group.recipients.map((r: any) => ({
        userId: String(r.userId ?? r.user_id ?? ''),
        email: !!r.email,
        sms: !!r.sms,
        push: !!r.push,
      }));
      setRecipients(mappedRecipients);
    }
  }, [group, users]);

  const handleSave = async () => {
    if (isSaving) return;
    if (recipients.length > 0) {
      for (let i = 0; i < recipients.length; i++) {
        const raw = String(recipients[i].userId ?? '').trim();
        const uid = raw === '' ? NaN : Number.parseInt(raw, 10);
        if (!Number.isFinite(uid)) {
          alert(`Recipient row ${i + 1}: select a valid user before saving (SMS/email require a user).`);
          return;
        }
      }
    }
    const recipientsForApi = recipients.map((r) => {
      const raw = String(r.userId ?? '').trim();
      const user_id = Number.parseInt(raw, 10);
      return { user_id, userId: user_id, email: !!r.email, sms: !!r.sms, push: !!r.push };
    });
    const allWindows = Object.entries(dayWindows).flatMap(([d, wins]) =>
      wins.map(w => ({ day: Number(d), start: w.start, end: w.end }))
    );
    const schedulePayload: AlertSchedule | null =
      scheduleEnabled && allWindows.length > 0
        ? { enabled: true, windows: allWindows }
        : null;
    const payload = {
      name,
      sensors: sensorIds,
      recipients: recipientsForApi,
      notify_temperature: notifyTemperature,
      notify_battery: notifyBattery,
      notify_missing_data: notifyMissingData,
      schedule: schedulePayload,
    };
    setIsSaving(true);
    try {
      if (group && group.id != null) {
        await updateRemoteAlertGroup({ id: Number(group.id), body: payload }).unwrap();
      } else {
        await addRemoteAlertGroup(payload).unwrap();
      }
      onSave();
    } catch (error) {
      alert('Failed to save alert group: ' + formatRtkMutationError(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSensorToggle = (sensorId: number) => {
    setSensorIds(prev =>
      prev.includes(sensorId) ? prev.filter(id => id !== sensorId) : [...prev, sensorId]
    );
  };

  const removeSensor = (sensorId: number) => {
    setSensorIds(prev => prev.filter(id => id !== sensorId));
  };

  const addRecipient = () => {
    if (users.length > 0) {
      setRecipients(prev => [...prev, { userId: String(users[0].id), email: true, sms: false, push: false }]);
    }
  };

  const handleRecipientChange = (index: number, field: keyof AlertRecipient, value: any) => {
    const newRecipients = [...recipients];
    (newRecipients[index] as any)[field] = value;
    setRecipients(newRecipients);
  };

  const removeRecipient = (index: number) => {
    setRecipients(prev => prev.filter((_, i) => i !== index));
  };

  // Mon-Sun display order (European convention)
  const DAY_CONFIG = [
    { label: 'Mon', day: 1 }, { label: 'Tue', day: 2 }, { label: 'Wed', day: 3 },
    { label: 'Thu', day: 4 }, { label: 'Fri', day: 5 }, { label: 'Sat', day: 6 },
    { label: 'Sun', day: 0 },
  ];

  const toggleDayActive = useCallback((day: number) => {
    setDayWindows(prev => {
      const wins = prev[day] ?? [];
      return { ...prev, [day]: wins.length > 0 ? [] : [{ start: '08:00', end: '20:00' }] };
    });
  }, []);

  const addWindow = useCallback((day: number) => {
    setDayWindows(prev => ({
      ...prev,
      [day]: [...(prev[day] ?? []), { start: '08:00', end: '20:00' }],
    }));
  }, []);

  const removeWindow = useCallback((day: number, idx: number) => {
    setDayWindows(prev => ({
      ...prev,
      [day]: (prev[day] ?? []).filter((_, i) => i !== idx),
    }));
  }, []);

  const updateWindow = useCallback((day: number, idx: number, field: 'start' | 'end', value: string) => {
    setDayWindows(prev => {
      const wins = [...(prev[day] ?? [])];
      wins[idx] = { ...wins[idx], [field]: value };
      return { ...prev, [day]: wins };
    });
  }, []);

  const zoneSensorList = useMemo(() => {
    if (!zoneSensors.length || !selectedZoneId) return [];
    return zoneSensors.map(s => ({ id: s.sensor_id, name: s.sensor_name }));
  }, [zoneSensors, selectedZoneId]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-start sm:items-center z-50 p-4 overflow-y-auto">
      <div className="bg-app-panel dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl my-4 sm:my-0 flex flex-col max-h-[calc(100dvh-2rem)]">
        {/* Sticky header */}
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{group ? 'Edit' : 'Add'} Alert Group</h2>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Group Name */}
          <div>
            <label htmlFor="groupName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Group Name
            </label>
            <input
              type="text"
              id="groupName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block w-full px-3 py-2 bg-app-inset dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm text-gray-900 dark:text-white"
            />
          </div>

          {/* Selected Sensors overview */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Selected Sensors{sensorIds.length > 0 && <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-gray-400">({sensorIds.length})</span>}
            </h3>
            {sensorIds.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 italic">No sensors selected yet. Use the zone browser below to add sensors.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/30 max-h-32 overflow-y-auto">
                {sensorIds.map(id => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-300"
                  >
                    {sensorNameMap.get(id) ?? `Sensor #${id}`}
                    <button
                      type="button"
                      onClick={() => removeSensor(id)}
                      className="flex items-center justify-center w-3.5 h-3.5 rounded-full text-indigo-500 hover:bg-indigo-200 hover:text-indigo-700 dark:hover:bg-indigo-800 dark:hover:text-indigo-100 transition-colors leading-none"
                      aria-label={`Remove sensor ${sensorNameMap.get(id) ?? id}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Zone browser — add / remove sensors by zone */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Add Sensors by Zone</h3>
            <select
              id="zoneSelect"
              value={selectedZoneId ?? ''}
              onChange={e => setSelectedZoneId(e.target.value ? Number(e.target.value) : null)}
              className="block w-full px-3 py-2 mb-2 bg-app-inset dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select a zone…</option>
              {zones.map(zone => (
                <option key={zone.zone_id} value={zone.zone_id}>{zone.zone_name}</option>
              ))}
            </select>
            <div key={selectedZoneId} className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/30 p-3 max-h-44 overflow-y-auto">
              {sensorsLoading ? (
                <span className="text-sm text-gray-400">Loading sensors…</span>
              ) : zoneSensorList.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {zoneSensorList.map(sensor => (
                    <label key={sensor.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sensorIds.includes(sensor.id)}
                        onChange={() => handleSensorToggle(sensor.id)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{sensor.name}</span>
                    </label>
                  ))}
                </div>
              ) : selectedZoneId ? (
                <span className="text-sm text-gray-400">No sensors in this zone.</span>
              ) : (
                <span className="text-sm text-gray-400">Select a zone to see its sensors.</span>
              )}
            </div>
          </div>

          {/* Alert Types */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Alert Types</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Choose which alarm types trigger notifications for this group.
            </p>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyTemperature}
                  onChange={e => setNotifyTemperature(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Temperature (high / low)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyBattery}
                  onChange={e => setNotifyBattery(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Battery low</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyMissingData}
                  onChange={e => setNotifyMissingData(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Missing / stale data</span>
              </label>
            </div>
          </div>

          {/* Recipients */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Recipients</h3>
            <div className="space-y-2 max-h-52 overflow-y-auto">
              {recipients.map((recipient, index) => {
                const selectedUser = users.find(u => String(u.id) === String(recipient.userId));
                return (
                <div key={index} className="flex flex-col gap-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900/30">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <select
                    value={recipient.userId}
                    onChange={(e) => handleRecipientChange(index, 'userId', e.target.value)}
                    className="block w-full sm:flex-1 px-3 py-2 bg-app-inset dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {usersLoading ? (
                      <option>Loading users…</option>
                    ) : users.length > 0 ? (
                      users.map(u => (
                        <option key={u.id} value={String(u.id)}>{u.firstName} {u.lastName}</option>
                      ))
                    ) : (
                      <option>No users found</option>
                    )}
                  </select>
                  <div className="flex items-center gap-3 sm:gap-2 flex-shrink-0">
                    <label className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={recipient.email}
                        onChange={(e) => handleRecipientChange(index, 'email', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      Email
                    </label>
                    <label className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={recipient.sms}
                        onChange={(e) => handleRecipientChange(index, 'sms', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      SMS
                    </label>
                    <label className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={recipient.push}
                        onChange={(e) => handleRecipientChange(index, 'push', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      Push
                    </label>
                    <button
                      type="button"
                      onClick={() => removeRecipient(index)}
                      className="text-red-500 hover:text-red-700 font-bold text-lg leading-none flex-shrink-0"
                      aria-label="Remove recipient"
                    >
                      &times;
                    </button>
                  </div>
                  </div>
                  {selectedUser && (
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400 pl-0.5">
                      <span>{selectedUser.email || <em>no email</em>}</span>
                      <span>{selectedUser.phoneNumber || <em>no phone</em>}</span>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={addRecipient}
              className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              + Add Recipient
            </button>
          </div>

          {/* Notification Schedule */}
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-1">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notification Schedule</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {scheduleEnabled ? 'Only notify during the configured windows (Europe/London time).' : 'Always notify — no time restrictions.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setScheduleEnabled(v => !v)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                  scheduleEnabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
                aria-pressed={scheduleEnabled}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  scheduleEnabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {scheduleEnabled && (
              <div className="mt-3 space-y-1">
                {DAY_CONFIG.map(({ label, day }) => {
                  const wins = dayWindows[day] ?? [];
                  const active = wins.length > 0;
                  return (
                    <div key={day} className={`rounded-lg px-3 py-2 ${active ? 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800' : 'bg-gray-50 dark:bg-gray-700/40'}`}>
                      <div className="flex items-start gap-3">
                        {/* Day toggle button */}
                        <button
                          type="button"
                          onClick={() => toggleDayActive(day)}
                          className={`mt-0.5 w-10 py-1 text-xs font-semibold rounded flex-shrink-0 transition-colors ${
                            active
                              ? 'bg-indigo-600 text-white'
                              : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-500'
                          }`}
                        >
                          {label}
                        </button>

                        {active ? (
                          <div className="flex-1 space-y-1.5">
                            {wins.map((w, wi) => (
                              <div key={wi} className="flex items-center gap-1.5 flex-wrap">
                                <input
                                  type="time"
                                  value={w.start}
                                  onChange={e => updateWindow(day, wi, 'start', e.target.value)}
                                  className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                                <span className="text-xs text-gray-400">→</span>
                                <input
                                  type="time"
                                  value={w.end}
                                  onChange={e => updateWindow(day, wi, 'end', e.target.value)}
                                  className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                                {w.start >= w.end && (
                                  <span className="text-[10px] text-amber-500">end must be after start</span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => removeWindow(day, wi)}
                                  className="text-red-400 hover:text-red-600 text-base leading-none px-1"
                                  title="Remove this window"
                                >×</button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addWindow(day)}
                              className="text-xs text-indigo-500 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                            >
                              + Add window
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">Inactive — click day to enable</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {Object.values(dayWindows).every(ws => ws.length === 0) && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 pt-1">Enable at least one day to use the schedule.</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sticky footer */}
        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemoteAlertGroupDialog;
